import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerBody,
  DrawerCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import axios from "axios";
import { BsUpload } from "react-icons/bs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Input } from "@chakra-ui/react";

// The date field holds dd/mm/yyyy until it is edited, then yyyy-mm-dd, or "" when cleared.
const toSlideDate = (value, currentDate) => {
  const picked = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (picked) {
    const [, year, month, day] = picked;
    return `${day}/${month}/${year}`;
  }
  return /^\d{2}\/\d{2}\/\d{4}$/.test(value) ? value : currentDate;
};

function GalleryUpdate({ slide, onUpdate, check, modalCheck }) {
  const initialValues = {
    image: "",
    date: slide.date,
    title: slide.title || "null",
    text: slide.text || "null",
  };
  const {
    isOpen,
    onOpen,
    onClose
  } = useDisclosure();
  useEffect(() => {
    if (check) {
      onOpen()
    }
  }, [check])
  const inputFileRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(slide.imageSrc);


  const handleUploadButtonClick = () => {
    inputFileRef.current.click();
  };

  const handleImageChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setFieldValue("image", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (values, actions) => {
    try {
      // Start the submit animation
      actions.setSubmitting(true);

      // Simulate a delay to show the animation
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Upload the image to Cloudinary and get the secure URL
      let imageUrl = slide.imageSrc;
      if (values.image) {
        const formData = new FormData();
        formData.append("file", values.image);
        formData.append("upload_preset", "tekxwxfz");// Replace with your Cloudinary unsigned upload preset
        const response = await axios.post(
          `https://api.cloudinary.com/v1_1/dzidj8k7j/auto/upload`,
          formData
        );
        imageUrl = response.data.secure_url;
      }
      const formattedDate = toSlideDate(values.date, slide.date);
      values.date = formattedDate;

      // Perform the update logic here using the updated data
      const updatedSlide = { ...slide, ...values, imageSrc: imageUrl };
      onUpdate(updatedSlide);

      // Display success toast
      toast.success("Slide updated successfully!");

      actions.setSubmitting(false); // Stop the submit animation
      onClose(); // Close the update popup
    } catch (error) {
      console.error(error);

      // Display error toast
      toast.error("Error updating the slide.");

      actions.setSubmitting(false); // Stop the submit animation on error
    }
  };

  return (
    <Drawer size="md" isOpen={isOpen} onClose={() => {
      onClose()
      modalCheck()
    }}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>Update Gallery Image</DrawerHeader>
        <DrawerCloseButton />
        <DrawerBody>
          <Formik initialValues={initialValues} onSubmit={handleSubmit} >
            {(props) => (
              <Form className="Form">
                {/* Image upload */}
                <Field name="image">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.image && form.touched.image}
                    >
                      <FormLabel>Image</FormLabel>
                      <Button
                        onClick={handleUploadButtonClick}
                        leftIcon={<BsUpload />}
                        variant="outline"
                      >
                        Upload Image
                      </Button>
                      <Box display="none">
                        <input
                          type="file"
                          accept="image/*"
                          ref={inputFileRef}
                          onChange={(event) =>
                            handleImageChange(event, props.setFieldValue)
                          }
                        />
                      </Box>
                      {imagePreview && (
                        <Box mt={4}>
                          <img
                            src={imagePreview}
                            alt="Image Preview"
                            style={{ maxWidth: "200px", borderRadius: ".5rem" }}
                          />
                        </Box>
                      )}
                      <FormErrorMessage>
                        {form.errors.image}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* Date input */}
                <Field name="date">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.date && form.touched.date}
                      mt={4}
                    >
                      <FormLabel>Date</FormLabel>
                      <Input
                        {...field}
                        type="date"
                        value={field.value.split('/').reverse().join('-')} // Convert the date format
                        placeholder="Date"
                      />
                      <FormErrorMessage>{form.errors.date}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>


                {/* Title input */}
                <Field name="title">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.title && form.touched.title}
                      mt={4}
                    >
                      <FormLabel>Title</FormLabel>
                      <Input {...field} type="text" placeholder="Title" />
                      <FormErrorMessage>{form.errors.title}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* Text input */}
                <Field name="text">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.text && form.touched.text}
                      mt={4}
                    >
                      <FormLabel>Text</FormLabel>
                      <Textarea {...field} placeholder="Text" />
                      <FormErrorMessage>{form.errors.text}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {/* Submit button */}
                <Button
                  w="100%"
                  mt={4}
                  isLoading={props.isSubmitting}
                  type="submit"
                >
                  Update
                </Button>
              </Form>
            )}
          </Formik>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" mr={3} onClick={() => {
            onClose()
            modalCheck()
          }}>
            Close
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default GalleryUpdate;
